import { TableContainer, Table, TableHead, TableRow, TableCell, TableBody, TextField, Typography, Stack, Box, Grid, CircularProgress } from '@mui/material';
import { theme } from '../../../theme';
import { ItemWasteChecksheetData, SaveStatus } from '../interfaces/ItemWasteChecksheetData';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import { Category } from '../interfaces/Category';
import { WasteChecksheetDetailContext, WasteChecksheetDetailValues } from '../contexts/WasteChecksheetDetailContext';
import { useContext } from 'react';

interface WasteChecksheetTableProps {
  category: Category;
  categoryIndex: number;
}

const COLUMN_WIDTHS: Record<string, string> = {
  itemNumber: '15%',
  itemDescription: '25%',
  lowestUom: '10%',
  pastDueQty: '10%',
  sampledQty: '10%',
  damagedQty: '10%',
  storeUseQty: '10%',
  total: '10%',
};

const QuantityInput = ({ id, value, disabled, onChange, label, setWidth, status }: { id: string; value: number | string; disabled: boolean; onChange: (value: string) => void; label: string | null; setWidth: boolean; status: SaveStatus }) => (
  <Box sx={{ position: 'relative', display: 'inline-block' }}>
    <TextField
      label={label}
      type="number"
      size="small"
      id={id}
      variant="outlined"
      disabled={disabled || status === 'saving'}
      sx={{
        minWidth: setWidth ? '60px' : undefined,
        width: '100%',
      }}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      inputProps={{ min: 0 }}
      InputLabelProps={{ shrink: true }}
      InputProps={{
        inputProps: { sx: { textAlign: 'right', pr: 2 } },
      }}
    />
    {/* Status indicators */}
    {status === 'saving' && (
      <Box className='hide-on-print'
        sx={{ position: 'absolute', top: -4, right: -4 }}>
        <CircularProgress size={16} />
      </Box>
    )}
    {status === 'saved' && (
      <Box className='hide-on-print'
        sx={{ position: 'absolute', top: -4, right: -4, color: theme.palette.success.main }}>
        <CloudDoneIcon sx={{ fontSize: 16 }} />
      </Box>
    )}
    {status === 'error' && (
      <Box className='hide-on-print'
        sx={{ position: 'absolute', top: -4, right: -4, color: theme.palette.error.main }}>
        <ErrorIcon sx={{ fontSize: 16 }} />
      </Box>
    )}
    {status === 'idle' && (
      <Box className='hide-on-print'
        sx={{ position: 'absolute', top: -6, right: -6, color: theme.palette.warning.main }}>
        <WarningIcon sx={{ fontSize: 18 }} />
      </Box>
    )}
  </Box>

);

const WasteChecksheetTable = ({ category, categoryIndex }: WasteChecksheetTableProps) => {

  const { t,
    isArchived,
    hasPermissionToWrite,
    isMobile,
    isEditMode,
    handleItemFocus,
    handleQuantityChange,
    isPrinting,
  } = useContext(WasteChecksheetDetailContext) as WasteChecksheetDetailValues;

  const calculateTotal = (itemWaste: ItemWasteChecksheetData) => {
    return (itemWaste.pastDueQty ?? 0) + (itemWaste.sampledQty ?? 0) + (itemWaste.damagedQty ?? 0) + (itemWaste.storeUseQty ?? 0);
  };

  if (isMobile && !isPrinting) {
    return (
      <Box sx={{
        mb: 6,
        borderBottom: '1px solid',
        borderBottomColor: theme.palette.custom.gray[300],
      }}>
        <Box sx={{
          color: theme.palette.custom.gray[600],
          fontWeight: 600,
          borderBottom: '1px solid',
          borderBottomColor: theme.palette.custom.gray[300],
          borderTop: '1px solid',
          borderTopColor: theme.palette.custom.gray[300],
          backgroundColor: theme.palette.custom.gray[200],
          paddingTop: '8px',
          paddingBottom: '8px',
          paddingLeft: '8px',
          paddingRight: '8px',
        }}>
          <Typography variant="textMD"
            fontWeight={600}>{category.name}</Typography>
        </Box>
        {category.items.map((item, itemIndex) => (
          <Box key={item.id}
            onFocus={isEditMode ? () => handleItemFocus(item.id ?? 0) : undefined}
            sx={{
              p: 4,
              borderBottom: itemIndex === category.items.length - 1 ? 'none' : '1px solid',
              borderBottomColor: theme.palette.custom.gray[300],
            }}>
            <Grid container
              spacing={4}>
              <Grid item
                xs={9}>
                <Stack>
                  <Typography variant="textMD"
                    sx={{
                      color: theme.palette.custom.gray[600],
                      wordBreak: 'break-word',  // Ensures wrapping between words
                      overflowWrap: 'break-word', // Alternative for better compatibility
                      whiteSpace: 'normal', // Ensures text wraps instead of staying in a single line 
                    }}
                    fontWeight={600}> {item.description}</Typography>
                  <Typography variant="textXS"
                    sx={{ color: theme.palette.custom.gray[600] }}>{t('details.itemNumber')}: {item.itemNumber}</Typography>
                  <Typography variant="textXS"
                    sx={{ color: theme.palette.custom.gray[600] }}>{t('details.lowestUom')}: {item.uom}</Typography>
                </Stack>
              </Grid>
              <Grid item
                xs={3}
                sx={{ display: 'flex', alignItems: 'center' }}>
                <TextField label={t('details.total')}
                  sx={{ minWidth: '60px' }}
                  type="number"
                  size="small"
                  id='total'
                  variant="outlined"
                  disabled
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    inputProps: { sx: { textAlign: 'right', pr: 2 } },
                  }}
                  value={calculateTotal(item)} />
              </Grid>
              {(['pastDueQty', 'sampledQty', 'damagedQty', 'storeUseQty'] as const).map((field) => (
                <Grid item
                  xs={3}
                  key={field}
                  style={{ marginTop: 8 }}>
                  <QuantityInput label={t(`details.${field}`)}
                    setWidth={false}
                    id={field}
                    value={item[field] ?? ''}
                    disabled={isArchived || !hasPermissionToWrite || item.isDepositFeeItem == true}
                    onChange={(value) => handleQuantityChange(categoryIndex, itemIndex, field, value)}
                    status={item[`${field}SaveStatus`] ?? 'standard'}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        ))
        }
      </Box >
    );
  }

  return (
    <Box sx={{ borderTop: '1px solid', borderBottom: '1px solid', borderColor: theme.palette.custom.gray[300], mb: 6 }}>
      <Box sx={{ backgroundColor: theme.palette.custom.gray[200], mb: 2, p: 4, fontWeight: 500, borderBottom: '1px solid', borderColor: theme.palette.custom.gray[300] }}
        className="title">{category.name}
      </Box>
      <TableContainer id='waste-checksheet-items-table-container'
        sx={{ maxWidth: '100%', borderTop: '1px solid', borderColor: theme.palette.custom.gray[300] }}
        className='waste-checksheet-categories'
        key={category.name}>
        <Table>
          <TableHead>
            <TableRow className='normal-field'>
              {Object.keys(COLUMN_WIDTHS).map((field) => (
                <TableCell key={field}
                  align="left"
                  style={{ width: COLUMN_WIDTHS[field] }}
                  className={field}>{t(`details.${field}`)}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {category.items.sort((a, b) => {
              //sort deposit and deposit fee items so they are close in the UI
              if (a.isDepositFeeItem === b.isDepositFeeItem) {
                return b.isDepositItem === a.isDepositItem ? 0 : b.isDepositItem ? 1 : -1;
              }
              return b.isDepositFeeItem ? 1 : -1;
            }).map((item, itemIndex) => (
              <TableRow key={item.id}
                className='normal-field'
                onFocus={isEditMode ? () => handleItemFocus(item.id ?? 0) : undefined}>
                <TableCell className="title">{item.itemNumber}</TableCell>
                <TableCell className="description"
                  style={{
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',  // Breaks long words when necessary
                    overflowWrap: 'break-word',
                    hyphens: 'auto',
                  }}>{item.description}</TableCell>
                <TableCell className="uom">{item.uom}</TableCell>
                {(['pastDueQty', 'sampledQty', 'damagedQty', 'storeUseQty'] as const).map((field) => (
                  <TableCell key={field}>
                    <QuantityInput label={null}
                      setWidth={true}
                      id={field}
                      value={item.isDepositFeeItem ? '' : item[field] ?? ''}
                      disabled={isArchived || !hasPermissionToWrite || item.isDepositFeeItem == true}
                      onChange={(value) => handleQuantityChange(categoryIndex, itemIndex, field, value)}
                      status={item[`${field}SaveStatus`] ?? 'standard'}
                    />
                  </TableCell>
                ))}
                <TableCell>
                  <TextField sx={{ minWidth: '60px', width: '100%' }}
                    InputLabelProps={{ shrink: true }}
                    type="number"
                    size="small"
                    id='total'
                    variant="outlined"
                    disabled
                    InputProps={{
                      inputProps: { sx: { textAlign: 'right', pr: 2 } },
                    }}
                    value={calculateTotal(item)} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer >
    </Box >
  );
};

export default WasteChecksheetTable;
